/**
 * usePriorityActions - live wire-up to the server-side ranked Posture queue.
 *
 * Backed by GET /api/posture/priority-actions/tenant/{tenantId}, which
 * implements the curation doctrine in
 * .agents/Tasks/High Priority/operations-priority-alerts-curation.md §4:
 *
 *   - Action-shaped (one row per recommended fix, not per event)
 *   - Impact-ranked using severity x policy_weight x frameworks x log(scope+1)
 *   - Capped at 6 rows per request, optionally narrowed per pillar
 *
 * The hook is intentionally thin: ranking and grouping live server-side so
 * they can be unit-tested in C# and reused by future surfaces. Filter chips
 * in PriorityQueue refetch with the requested pillar so a per-pillar filter
 * shows up to 6 ranked actions for that pillar (rather than narrowing a
 * single all-pillar fetch which could be empty for a given pillar).
 */

import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PriorityAction } from '@/lib/posture-brief'

const STALE_MS = 5 * 60 * 1000

export type PriorityActionsPillarFilter = 'all' | 'compliance' | 'bpa' | 'identity'

export interface UsePriorityActionsOptions {
  tenantId: string | undefined
  /** Cap returned rows. Endpoint clamps to [1, 20] server-side. Defaults to 6. */
  maxCount?: number
  /** Pillar narrowing. Pass 'all' (or omit) for the cross-pillar top N. */
  pillar?: PriorityActionsPillarFilter
}

export interface UsePriorityActionsResult {
  actions: PriorityAction[]
  isLoading: boolean
  error: Error | null
}

export function usePriorityActions(
  options: UsePriorityActionsOptions,
): UsePriorityActionsResult {
  const { tenantId, maxCount = 6, pillar = 'all' } = options

  const query = useQuery({
    queryKey: ['priority-actions', tenantId, maxCount, pillar],
    queryFn: () =>
      api.getPriorityActions(tenantId!, {
        limit: maxCount,
        pillar,
      }),
    enabled: !!tenantId,
    staleTime: STALE_MS,
  })

  return {
    actions: query.data ?? [],
    isLoading: !!tenantId && query.isLoading,
    error: (query.error as Error | null) ?? null,
  }
}
